/* ----------------------------------------------------------------------------
 * useVoiceLease — the cross-client single-active-voice-session lease hook
 * (voice residual #3). A THIN wiring shell over the pure leaseMachine core:
 *   - acquire-BEFORE-arm: the wake_host_gate mounts the engine only when this
 *     hook reports canArm (held OR fail-open), never before an acquire attempt.
 *   - 15s heartbeat (renew while holding); 'other' clients keep polling so they
 *     CLAIM within <= TTL once the current holder disappears.
 *   - release on deactivate (mic-kill / Standard / unmount) + best-effort
 *     keepalive release on pagehide. The 45s server TTL is the crash backstop.
 *   - FAIL-OPEN: any lease API error => gate 'open' => arm as TODAY, with a
 *     background re-acquire (capped backoff). A single-device user NEVER loses
 *     voice to an arbiter blip.
 *   - requestTakeover(): explicit "Listen here" — seizes the lease; the old
 *     holder yields on its next heartbeat.
 *
 * The cid is the SHARED per-FE-session id (voiceSessionCid) so a client's diag
 * beacons and its lease token are the same token. PTT never uses this hook.
 * -------------------------------------------------------------------------- */
import { useCallback, useEffect, useRef, useState } from 'react';
import { voiceBeacon, voiceSessionCid } from '../net/voiceDiag.ts';
import { acquireLease, renewLease, releaseLease, releaseLeaseKeepalive } from '../net/voiceLeaseApi.ts';
import {
  LEASE_HEARTBEAT_MS,
  chooseLeaseOp,
  deriveClientLabel,
  failOpenView,
  interpretLeaseResponse,
  leaseAllowsArm,
  nextRetryDelayMs,
  type LeaseGate,
  type LeaseView,
} from './leaseMachine.ts';

export interface UseVoiceLeaseResult {
  /** the current lease gate for this client. */
  gate: LeaseGate;
  /** the OTHER holder's short label (pill copy) — null unless gate==='other'. */
  holderLabel: string | null;
  /** may this client arm hands-free wake right now? (held OR fail-open) */
  canArm: boolean;
  /** explicit user takeover ("Listen here") — seizes the lease on the next tick. */
  requestTakeover: () => void;
}

/**
 * @param userId  the human's id (lease is user-scoped; null => inert).
 * @param active  pre-lease wake eligibility (wakeEligible && !micKilled). When
 *                false the hook releases and reports fail-open/inert.
 */
export function useVoiceLease(userId: string | number | null, active: boolean): UseVoiceLeaseResult {
  const [view, setView] = useState<LeaseView>({ gate: 'open', holderLabel: null });

  // Stable per-session cid (shared with diag beacons) + a one-time device label.
  const cidRef = useRef<string>('');
  if (cidRef.current === '') cidRef.current = voiceSessionCid();
  const labelRef = useRef<string>('');
  if (labelRef.current === '') {
    labelRef.current =
      typeof navigator !== 'undefined'
        ? deriveClientLabel(navigator.userAgent ?? '', (navigator as unknown as { platform?: string }).platform ?? '')
        : 'this device';
  }

  const takeoverPendingRef = useRef(false);
  const tickRef = useRef<() => void>(() => {});

  const requestTakeover = useCallback(() => {
    takeoverPendingRef.current = true;
    tickRef.current();
  }, []);

  useEffect(() => {
    if (!active || userId === null || userId === undefined) {
      setView({ gate: 'open', holderLabel: null });
      return;
    }

    let cancelled = false;
    let held = false;
    let retryDelay = 0;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const cid = cidRef.current;
    const label = labelRef.current;

    const clearHeartbeat = () => {
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
    };
    const clearRetry = () => {
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    };
    const startHeartbeat = () => {
      if (heartbeat) return;
      heartbeat = setInterval(() => { void tick(); }, LEASE_HEARTBEAT_MS);
    };

    const applyView = (v: LeaseView) => {
      if (cancelled) return;
      held = v.gate === 'held';
      setView(v);
      voiceBeacon('voice_lease', { gate: v.gate, holder_is_self: v.gate === 'held', cid });
    };

    const tick = async () => {
      if (cancelled) return;
      const op = chooseLeaseOp(held, takeoverPendingRef.current);
      takeoverPendingRef.current = false;
      try {
        const res = op.kind === 'renew' ? await renewLease(cid) : await acquireLease(cid, label, op.takeover);
        if (cancelled) return;
        const v = interpretLeaseResponse(res);
        applyView(v);
        clearRetry();
        retryDelay = 0;
        startHeartbeat(); // held => renew each beat; other => poll to claim on holder-exit
      } catch (err) {
        if (cancelled) return;
        // FAIL-OPEN: arm as today. NOT a silent swallow — the reason is beaconed
        // (voice_lease_failopen) so a capture exercise shows every arbiter blip.
        voiceBeacon('voice_lease_failopen', { op: op.kind, reason: (err as Error)?.name ?? 'error', cid });
        applyView(failOpenView());
        clearHeartbeat();
        retryDelay = nextRetryDelayMs(retryDelay);
        clearRetry();
        retryTimer = setTimeout(() => { void tick(); }, retryDelay);
      }
    };

    tickRef.current = () => { void tick(); };
    void tick(); // acquire-BEFORE-arm (immediate)

    const onPageHide = () => { releaseLeaseKeepalive(cid); };
    if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      clearHeartbeat();
      clearRetry();
      if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
      tickRef.current = () => {};
      if (held) { void releaseLease(cid).catch(() => {}); } // release only OUR lease
    };
  }, [active, userId]);

  return {
    gate: view.gate,
    holderLabel: view.holderLabel,
    canArm: leaseAllowsArm(view.gate),
    requestTakeover,
  };
}
